import { useState } from 'react';
import { useAnalyzer } from './useAnalyzer';
import { isValidSolanaAddress } from './analyzerCore';
import { ScoreBadge, Pnl, WinBar, Skel, JOURS, fmtUsd, fmtHold, heureLabel, shortAddr, verdictLabel, verdictCls } from './anUi';

// higher: true = plus grand gagne ; false = plus petit gagne ; null = ligne non scorée
const ROWS = [
  { label: 'Score', get: d => d.verdict.score, fmt: v => v, higher: true },
  { label: 'ROI', get: d => d.overallRoi, fmt: v => <Pnl value={v} />, higher: true },
  { label: 'Win Rate', get: d => (d.totalTrades > 0 ? d.winRate : null), fmt: v => <WinBar value={v} />, higher: true },
  { label: 'PNL total', get: d => (d.totalTrades > 0 ? d.pnlUsd : null), fmt: v => fmtUsd(v, true), higher: true },
  { label: 'Trades', get: d => d.totalTrades, fmt: v => v, higher: true },
  { label: 'Gain moy.', get: d => (d.avgGainUsd > 0 ? d.avgGainUsd : null), fmt: v => fmtUsd(v), higher: true },
  { label: 'Perte moy.', get: d => (d.avgLossUsd > 0 ? d.avgLossUsd : null), fmt: v => fmtUsd(v), higher: false },
  { label: 'R:R', get: d => d.rrRatio, fmt: v => v.toFixed(2), higher: true },
  { label: 'Streak max', get: d => d.maxStreak, fmt: v => v, higher: true },
  { label: 'Volume', get: d => d.volumeUsd, fmt: v => fmtUsd(v), higher: true },
  { label: 'Fréq. /jour', get: d => d.tradesPerDay, fmt: v => v.toFixed(1), higher: true },
  { label: 'Confiance', get: d => d.confidence.score, fmt: v => `${v}/100`, higher: true },
  { label: 'Jours actifs', get: d => d.daysActive, fmt: v => v, higher: true },
];

const UNSCORED = [
  { label: 'Durée moy.', get: d => fmtHold(d.dureeMoyMin) },
  { label: 'Meill. heure', get: d => heureLabel(d.heatmap.bestHeure), raw: d => d.heatmap.bestHeure },
  { label: 'Meill. jour', get: d => (d.heatmap.bestJour != null ? JOURS[d.heatmap.bestJour] : '—'), raw: d => d.heatmap.bestJour },
  { label: 'Recomm.', get: d => verdictLabel(d.verdict.recommendation), cls: d => verdictCls(d.verdict.recommendation) },
];

function winner(row, a, b) {
  const va = row.get(a), vb = row.get(b);
  if (va == null || vb == null || va === vb) return null;
  return (row.higher ? va > vb : va < vb) ? 'a' : 'b';
}

export default function CompareTab({ a, apiKey }) {
  const [input, setInput] = useState('');
  const [addrB, setAddrB] = useState('');
  const [runId, setRunId] = useState(0);
  const [err, setErr] = useState('');
  const { data: b, loading, error } = useAnalyzer(addrB, runId, apiKey);

  const go = () => {
    const v = input.trim();
    if (!isValidSolanaAddress(v)) { setErr('Adresse Solana invalide'); return; }
    if (v === a.address) { setErr('Choisissez un wallet différent'); return; }
    setErr(''); setAddrB(v); setRunId(r => r + 1);
  };
  const reset = () => { setAddrB(''); setInput(''); setRunId(0); setErr(''); };

  if (!addrB) {
    return (
      <div className="an-panel an-cmp-input">
        <div className="an-section-title"><span className="an-section-icon">⚔️</span>Comparer avec un autre wallet</div>
        <div className="an-muted an-small">Lance une analyse complète du wallet B (≈ 5 000 crédits Helius).</div>
        <div className="an-input-row">
          <input className="an-input" placeholder="Adresse du wallet B" value={input} onChange={e => { setInput(e.target.value); setErr(''); }} onKeyDown={e => e.key === 'Enter' && go()} />
          <button className="an-btn an-btn--accent" onClick={go}>GO</button>
        </div>
        {err && <div className="an-error-inline">{err}</div>}
      </div>
    );
  }
  if (loading) return <div className="an-panel an-stack"><Skel h={22} w="40%" /><Skel h={160} /><Skel h={160} /></div>;
  if (error || !b) {
    return <div className="an-panel"><div className="an-error-inline">{error || 'Analyse indisponible'}</div><button className="an-btn an-btn--ghost" onClick={reset}>Changer</button></div>;
  }

  const wins = { a: 0, b: 0 };
  ROWS.forEach(r => { const w = winner(r, a, b); if (w) wins[w]++; });
  const lead = wins.a === wins.b ? null : wins.a > wins.b ? 'a' : 'b';

  return (
    <div className="an-stack">
      <div className="an-cmp-head">
        <div className={`an-cmp-card${lead === 'a' ? ' an-cmp-card--lead' : ''}`}>
          <div className="an-muted an-small">Wallet A</div>
          <div className="an-id-addr">{shortAddr(a.address)}</div>
          <ScoreBadge score={a.verdict.score} />
          <div className="an-small">{wins.a} ligne(s) gagnée(s)</div>
        </div>
        <div className="an-cmp-vs">VS</div>
        <div className={`an-cmp-card${lead === 'b' ? ' an-cmp-card--lead' : ''}`}>
          <div className="an-muted an-small">Wallet B</div>
          <div className="an-id-addr">{shortAddr(b.address)}</div>
          <ScoreBadge score={b.verdict.score} />
          <div className="an-small">{wins.b} ligne(s) gagnée(s)</div>
        </div>
      </div>
      <div className="an-note">
        {lead ? <>Verdict : <b>Wallet {lead.toUpperCase()}</b> l'emporte ({wins[lead]} vs {wins[lead === 'a' ? 'b' : 'a']}).</> : <>Verdict : égalité ({wins.a} – {wins.b}).</>}
        {' '}<button className="an-btn an-btn--ghost an-btn--sm" onClick={reset}>Changer</button>
      </div>
      <div className="an-table-wrap">
        <table className="an-table an-cmp-table">
          <thead><tr><th>Critère</th><th>Wallet A</th><th>Wallet B</th></tr></thead>
          <tbody>
            {ROWS.map(r => {
              const w = winner(r, a, b);
              const cell = (d) => { const v = r.get(d); return v == null ? '—' : r.fmt(v); };
              return (
                <tr key={r.label} className="an-row">
                  <td>{r.label}</td>
                  <td className={w === 'a' ? 'an-cmp-win' : ''}>{cell(a)}{w === 'a' && ' 🏆'}</td>
                  <td className={w === 'b' ? 'an-cmp-win' : ''}>{cell(b)}{w === 'b' && ' 🏆'}</td>
                </tr>
              );
            })}
            {UNSCORED.map(r => {
              const same = r.raw && r.raw(a) === r.raw(b);
              return (
                <tr key={r.label} className="an-row an-row--unscored">
                  <td>{r.label}</td>
                  <td><span className={r.cls ? r.cls(a) : ''}>{r.get(a)}</span></td>
                  <td><span className={r.cls ? r.cls(b) : ''}>{r.get(b)}</span>{same && <span className="an-muted"> =</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
